import { useState } from 'react';
import { NavLink, matchPath, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  LayoutDashboard,
  Users,
  Building2,
  Briefcase,
  CheckSquare,
  FileText,
  Receipt,
  Calendar,
  CalendarClock,
  BookOpen,
  Car,
  Clock,
  X,
  Building,
  ClipboardList,
  Settings,
  LifeBuoy,
  CreditCard,
  Upload,
  FileUp,
  ListChecks,
  ListTodo,
} from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { useOpenItemsStore } from '../../store/useOpenItemsStore';
import { useTranslation } from '../../i18n';
import facilityFlowLogo from '../../assets/facilityflow-logo.svg';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavigationItem {
  nameKey: string;
  to: string;
  icon: typeof LayoutDashboard;
  roles?: ('Verwaltung' | 'Mitarbeiter')[];
  end?: boolean;
  /** Shows a count pill on the right; the count is read from the matching store. */
  badge?: 'openItems';
}

interface NavigationGroup {
  labelKey: string;
  collapsible?: boolean;
  items: NavigationItem[];
}

const COLLAPSED_GROUPS_STORAGE_KEY = 'ff.sidebar.collapsed';

function loadCollapsedGroups(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_GROUPS_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? new Set(parsed.filter((key): key is string => typeof key === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

function saveCollapsedGroups(collapsed: Set<string>) {
  try {
    localStorage.setItem(COLLAPSED_GROUPS_STORAGE_KEY, JSON.stringify([...collapsed]));
  } catch {
    // Storage unavailable (private mode, blocked site data): collapse state stays per-session.
  }
}

function isItemActive(item: NavigationItem, pathname: string) {
  return matchPath({ path: item.to, end: item.end ?? false }, pathname) !== null;
}

const navigationGroups: NavigationGroup[] = [
  {
    labelKey: 'navigation:groups.general',
    items: [
      { nameKey: 'navigation:menu.dashboard', to: '/', icon: LayoutDashboard, end: true },
      { nameKey: 'navigation:menu.openItems', to: '/open-items', icon: ListTodo, badge: 'openItems' },
    ],
  },
  {
    labelKey: 'navigation:groups.personal',
    items: [
      { nameKey: 'navigation:menu.myPlan', to: '/my-plan', icon: ClipboardList },
      { nameKey: 'navigation:menu.myChecklists', to: '/my-checklists', icon: ListChecks },
      { nameKey: 'navigation:menu.absences', to: '/absences', icon: CalendarClock, end: true },
      { nameKey: 'navigation:menu.timeClock', to: '/timeclock', icon: Clock, end: true },
    ],
  },
  {
    labelKey: 'navigation:groups.crm',
    collapsible: true,
    items: [
      { nameKey: 'navigation:menu.customers', to: '/customers', icon: Users, roles: ['Verwaltung'] },
      { nameKey: 'navigation:menu.objects', to: '/objects', icon: Building2, roles: ['Verwaltung'] },
      { nameKey: 'navigation:menu.offers', to: '/offers', icon: FileText, roles: ['Verwaltung'] },
    ],
  },
  {
    labelKey: 'navigation:groups.operations',
    collapsible: true,
    items: [
      { nameKey: 'navigation:menu.planning', to: '/planning', icon: Calendar },
      { nameKey: 'navigation:menu.tasks', to: '/tasks', icon: CheckSquare, roles: ['Verwaltung'] },
      { nameKey: 'navigation:menu.checklistProtocol', to: '/checklist-protocol', icon: ListChecks, roles: ['Verwaltung'] },
      { nameKey: 'navigation:menu.services', to: '/services', icon: Briefcase, roles: ['Verwaltung'] },
    ],
  },
  {
    labelKey: 'navigation:groups.hr',
    collapsible: true,
    items: [
      { nameKey: 'navigation:menu.employees', to: '/employees', icon: Users, roles: ['Verwaltung'] },
      { nameKey: 'navigation:menu.workScheduleModels', to: '/work-schedule-models', icon: Clock, roles: ['Verwaltung'] },
      { nameKey: 'navigation:menu.absencesAdmin', to: '/absences/admin', icon: CalendarClock, roles: ['Verwaltung'] },
      { nameKey: 'navigation:menu.timeClockAdmin', to: '/timeclock/admin', icon: Clock, roles: ['Verwaltung'] },
    ],
  },
  {
    labelKey: 'navigation:groups.fleet',
    collapsible: true,
    items: [
      { nameKey: 'navigation:menu.vehicles', to: '/vehicles', icon: Car, roles: ['Verwaltung'], end: true },
      { nameKey: 'navigation:menu.vehicleLogbook', to: '/vehicles/logbook', icon: BookOpen, roles: ['Verwaltung'] },
    ],
  },
  {
    labelKey: 'navigation:groups.finance',
    collapsible: true,
    items: [
      { nameKey: 'navigation:menu.invoices', to: '/invoices', icon: Receipt, roles: ['Verwaltung'] },
    ],
  },
  {
    labelKey: 'navigation:groups.system',
    collapsible: true,
    items: [
      { nameKey: 'navigation:menu.settings', to: '/settings', icon: Settings, roles: ['Verwaltung'], end: true },
      { nameKey: 'navigation:menu.subscription', to: '/settings/subscription', icon: CreditCard, roles: ['Verwaltung'] },
      { nameKey: 'navigation:menu.netzplanImport', to: '/import/netzplan', icon: Upload, roles: ['Verwaltung'] },
      { nameKey: 'navigation:menu.contractImport', to: '/import/contract', icon: FileUp, roles: ['Verwaltung'] },
      { nameKey: 'navigation:menu.support', to: '/support', icon: LifeBuoy, roles: ['Verwaltung'] },
    ],
  },
];

const superAdminNavigationItems: NavigationItem[] = [
  { nameKey: 'navigation:menu.tenants', to: '/tenants', icon: Building },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { employee, isSuperAdmin } = useAuthStore();
  const { t } = useTranslation(['navigation', 'common']);
  const { pathname } = useLocation();
  const [collapsedGroups, setCollapsedGroups] = useState(loadCollapsedGroups);

  const toggleGroup = (labelKey: string) => {
    setCollapsedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(labelKey)) {
        next.delete(labelKey);
      } else {
        next.add(labelKey);
      }
      saveCollapsedGroups(next);
      return next;
    });
  };

  const role = employee?.role as 'Verwaltung' | 'Mitarbeiter' | undefined;
  const isItemAllowed = (item: NavigationItem) =>
    !item.roles || (role && item.roles.includes(role));

  const filteredGroups = isSuperAdmin
    ? []
    : navigationGroups
        .map((group) => ({ ...group, items: group.items.filter(isItemAllowed) }))
        .filter((group) => group.items.length > 0);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed lg:sticky top-0 inset-y-0 left-0 z-30 w-[212px] bg-white border-r border-slate-200 h-screen transform transition-transform duration-200 ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Brand block */}
          <div className="flex items-center gap-2 px-4 h-14 border-b border-slate-200">
            <NavLink to="/" onClick={onClose} className="flex items-center flex-1">
              <img src={facilityFlowLogo} alt="FacilityFlow" className="h-10 w-auto" />
            </NavLink>
            <button
              onClick={onClose}
              className="lg:hidden p-1 rounded hover:bg-slate-50 text-slate-500"
              aria-label={t('common:actions.close')}
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-2">
            {isSuperAdmin ? (
              <div>
                {superAdminNavigationItems.map((item) => (
                  <SidebarLink key={item.nameKey} item={item} onClose={onClose} t={t} />
                ))}
              </div>
            ) : (
              filteredGroups.map((group) => (
                <SidebarGroup
                  key={group.labelKey}
                  group={group}
                  isCollapsed={
                    collapsedGroups.has(group.labelKey) &&
                    !group.items.some((item) => isItemActive(item, pathname))
                  }
                  onToggle={() => toggleGroup(group.labelKey)}
                  onClose={onClose}
                  t={t}
                />
              ))
            )}
          </nav>

          {/* Footer */}
          <div className="kicker mt-auto px-4 py-3 border-t border-slate-200">
            {`Version ${__APP_VERSION__}`}
          </div>
        </div>
      </aside>
    </>
  );
}

interface SidebarGroupProps {
  group: NavigationGroup;
  isCollapsed: boolean;
  onToggle: () => void;
  onClose: () => void;
  t: (key: string) => string;
}

function SidebarGroup({ group, isCollapsed, onToggle, onClose, t }: SidebarGroupProps) {
  const itemsId = `sidebar-group-${group.labelKey.replace(/\W/g, '-')}`;
  const items = (
    <div id={itemsId}>
      {group.items.map((item) => (
        <SidebarLink key={item.nameKey} item={item} onClose={onClose} t={t} />
      ))}
    </div>
  );

  if (!group.collapsible) {
    return (
      <div>
        <div className="kicker px-4 pt-5 pb-2">{t(group.labelKey)}</div>
        {items}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!isCollapsed}
        aria-controls={itemsId}
        className="kicker w-full flex items-center justify-between px-4 pt-5 pb-2 hover:text-slate-600"
      >
        <span>{t(group.labelKey)}</span>
        <ChevronDown
          className={`w-3 h-3 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
          strokeWidth={2}
        />
      </button>
      {!isCollapsed && items}
    </div>
  );
}

interface SidebarLinkProps {
  item: NavigationItem;
  onClose: () => void;
  t: (key: string) => string;
}

function SidebarLink({ item, onClose, t }: SidebarLinkProps) {
  const Icon = item.icon;
  const openItemsCount = useOpenItemsStore((s) => s.myOpenCount);
  const badgeCount = item.badge === 'openItems' ? openItemsCount : 0;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={() => onClose()}
      className={({ isActive }) =>
        `flex items-center gap-2.5 mx-2 px-3 py-2 rounded-lg text-[13px] transition-colors ${
          isActive
            ? 'bg-[#00A871] text-white font-medium'
            : 'text-slate-600 hover:bg-slate-50'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
          <span>{t(item.nameKey)}</span>
          {badgeCount > 0 && (
            <span
              className={`ml-auto min-w-[20px] px-1.5 rounded-full text-[11px] font-medium tabular-nums text-center ${
                isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
              }`}
            >
              {badgeCount}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
